import { useEffect, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import { useTranslation } from '@klv/hooks/useTranslation';
import Reaptcha from 'reaptcha';
import * as Yup from 'yup';
import { withOptimizely } from '@optimizely/react-sdk';

import { getStoredTrackingParams } from '@klv/shared/utils/attribution';
import {
  getUrlTrackingParams, getFirstPage, submitForm, handleFieldBlur,
  windowIsDefined,
} from '@klv/shared/utils/functions';
import { debouncedBeginEvent, gtmFormEvent, combineGclidAndGaIdValue } from '@klv/shared/utils/google-tag-manager';

import Button from '@klv/atoms/button';
import CheckboxInput from '@klv/molecules/form-field/checkbox';
import HiddenInput from '@klv/molecules/form-field/hidden';
import SelectInput from '@klv/molecules/form-field/select';
import TextInput from '@klv/molecules/form-field/text';

import CTALegalLanguage from '../cta-legal-language';
import AutofillSync from '../shared/autofill-sync';
import handleErrors from '../shared/handle-errors';
import handleSuccess from '../shared/handle-success';
import { getNumMarketingFields, ScrollToError } from '../utils';

import * as styles from '../styles/forms.module.scss';

import type { FocusEvent, FormEvent } from 'react';
import type { WithOptimizelyProps } from '@optimizely/react-sdk';
import type { AxiosError } from 'axios';
import type { IFormValidationSchema, IInitialFormValues, IStaticDemoForm } from '../types';


const DemoForm = ({
  formState, optimizely, optimizelyReadyTimeout, isServerSide, setFormState, setSuccess, settings, templateName, backgroundColor = 'dark', ...props
}: IStaticDemoForm & WithOptimizelyProps) => {
  const { t } = useTranslation();

  const urlTrackingParams = getUrlTrackingParams();
  const storedTrackingParams = getStoredTrackingParams();
  const recaptchaInstance = useRef<Reaptcha>(null);
  const errorElement = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    debouncedBeginEvent('demo');
    optimizely?.track?.('demo begin');
  }, []);

  const initialValues: IInitialFormValues = {
    full_name: formState?.full_name || '',
    email: formState?.email || '',
    company: formState?.company_url || '',
    phone: formState?.phone || '',
    company_url: formState?.company_url || '',
    company_size: formState?.company_size || '',
    field_1: Boolean(formState?.newsletter) || false,
    utm_source: urlTrackingParams?.utm_source || storedTrackingParams?.utm_source || '',
    utm_medium: urlTrackingParams?.utm_medium || storedTrackingParams?.utm_medium || '',
    utm_campaign: urlTrackingParams?.utm_campaign || storedTrackingParams?.utm_campaign || '',
    utm_content: urlTrackingParams?.utm_content || storedTrackingParams?.utm_content || '',
    utm_term: urlTrackingParams?.utm_term || storedTrackingParams?.utm_term || '',
    gclid: '',
    detail: urlTrackingParams?.detail || storedTrackingParams?.detail || '',
    wbraid: urlTrackingParams?.wbraid || storedTrackingParams?.wbraid || '',
    first_page: getFirstPage(),
    field_6: formState?.field_6 || '',
    field_9: formState?.field_9 || '',
    'g-recaptcha-response': '',
    preferred_language:  t('langCode_languageString'),
    preferred_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  initialValues.num_marketing_fields = getNumMarketingFields(initialValues) || 17;

  const initialValuesFields = Object.keys(initialValues).filter((fieldName) => fieldName.startsWith('field_'));
  const formStateAdditionalFields = Object.keys(formState).filter((fieldName) => fieldName.startsWith('field_')).filter((fieldName) => !initialValuesFields.includes(fieldName));

  if (formStateAdditionalFields?.length > 0) {
    formStateAdditionalFields.forEach((field) => {
      initialValues[field] = formState?.[field] || '';
    });
  }

  const validationSchemaBase: IFormValidationSchema = {
    full_name: Yup.string()
      .required(t('organisms_forms_fields_full_name_errors_required') || 'Please enter your name'),
    email: Yup.string()
      .email(t('organisms_forms_fields_email_errors_email') || 'Please make sure your email address includes a single @, followed by a valid domain (name@example.com)')
      .required(t('organisms_forms_fields_email_errors_required') || 'Required'),
    phone: Yup.string()
      .matches(/^[\d|+|(]+[)|\d|\s|-]*[\d]$/, t('organisms_forms_fields_phone_errors_matches') || 'Please make sure your phone number includes your area code or country code')
      .max(15, t('organisms_forms_fields_phone_errors_max') || 'Number must have 15 or fewer characters')
      .required(t('organisms_forms_fields_phone_errors_required') || 'Required'),
    company_url: Yup.string()
      .url(t('organisms_forms_fields_company_website_errors_url') || 'Please make sure your company website includes a valid domain (https://example.com)')
      .required(t('organisms_forms_fields_company_website_errors_required') || 'Required'),
    company_size: Yup.string()
      .required(t('organisms_forms_fields_company_size_errors_required') || 'Please select the number of contacts you have'),
    preferred_language: Yup.string(),
    preferred_timezone: Yup.string(),
    field_1: Yup.boolean(),
    utm_source: Yup.string(),
    utm_medium: Yup.string(),
    utm_campaign: Yup.string(),
    utm_content: Yup.string(),
    utm_term: Yup.string(),
    gclid: Yup.string(),
    detail: Yup.string(),
    wbraid: Yup.string(),
    first_page: Yup.string(),
    lead_source: Yup.string(),
    lead_source_detail: Yup.string(),
    num_marketing_fields: Yup.string(),
    field_6: Yup.string(),
    field_9: Yup.string(),
    field_14: Yup.string(),
    'g-recaptcha-response': Yup.string(),
  };

  // Create a reset function
  const resetRecaptcha = () => {
    recaptchaInstance.current?.reset();
  };

  return (
    <div {...props}>
      {errors?.length > 0 && (
        <div className={styles.errorContainer}>
          {errors.map((error: string) => (
            <ScrollToError key={error} innerRef={errorElement}>
              <div className={styles.formError}>
                {error}
              </div>
            </ScrollToError>
          ))}
        </div>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={Yup.object(validationSchemaBase)}
        onSubmit={async (values, formikBag) => {
          const googleClientId = await combineGclidAndGaIdValue(urlTrackingParams?.gclid);
          // need to set touched.product_interests to true manually since it's not getting picked up automagically.
          formikBag.setTouched({ 'product_interest': true });
          const irclickid = urlTrackingParams?.irclickid || storedTrackingParams?.irclickid || '';
          values.gclid = `${googleClientId}${irclickid ? `:${irclickid}` : ''}`;
          values.num_marketing_fields = getNumMarketingFields(values);

          if (values['g-recaptcha-response'] === '') {
            setErrors([t('organisms_forms_errors_recaptcha') || 'reCaptcha is required']);

            return false;
          }
          try {
            const { setFieldError } = formikBag;
            const urlBase = (process.env.NODE_ENV === 'production') ? 'https://www.klaviyo.com' : '';
            const response = await submitForm(`${urlBase}/ajax/public/request/demo`, values);
            if (response?.status !== 200) {
              setErrors([t('organisms_forms_errors_network') || 'Your request was not successful because of a network error. Please try again.']);
            }
            if (response?.data?.success) {
              handleSuccess(values, 'demo');
              optimizely?.track?.('demo complete', undefined, { ...optimizely?.user?.attributes, formComplete: true });

              if (window.qualified) {
                window?.qualified?.(
                  'saveFormData',
                  values,
                );
                
                window?.qualified?.('emitFormFill', 'custom');
              }

              gtmFormEvent(values, 'requestDemo', 'complete').then(() => {
                if (windowIsDefined()) {
                  setTimeout(() => {
                    if (settings?.redirectUrl) {
                      window.location.assign(settings.redirectUrl);
                    }
                  }, 500);
                }
              });
              if (!settings?.redirectUrl) {
                setErrors([]);
                setSuccess(true);
              }

              return response;
            }

            handleErrors(response?.data?.errors || [], setErrors, setFieldError, values, 'demo');
            resetRecaptcha();

            return false;
          } catch (error) {
            const knownError = error as AxiosError;
            window?.heap?.track?.('Form Submission Error', {
              code: knownError?.code ?? '',
              emailAddress: values?.email ?? '',
              formName: 'demo',
              host: process.env.GATSBY_HOST ?? '',
              message: knownError?.message ?? '',
              name: knownError?.name ?? '',
              rawJson: JSON.stringify(error) ?? '',
              stack: knownError?.stack ?? '',
              statusCode: knownError?.status ?? '',
            });
            setErrors([t('organisms_forms_errors_generic') || 'Your request was not successful. Please try again.']);
            resetRecaptcha();

            return false;
          }
        }}
      >
        {(formik) => (
          <Form id="form--demoRequest">
            <TextInput
              backgroundColor={backgroundColor}
              id="demoForm-fullName"
              label={t('organisms_forms_fields_full_name_label')}
              name="full_name"
              type="text"
              placeholder={t('organisms_forms_fields_full_name_placeholder') || 'Full name'}
              onBlur={(event: FocusEvent) => handleFieldBlur(event, formik, setFormState, 'requestDemo')}
              autofocus
            />

            <TextInput
              backgroundColor={backgroundColor}
              id="demoForm-email"
              label={t('organisms_forms_fields_email_label')}
              name="email"
              type="email"
              placeholder={t('organisms_forms_fields_email_placeholder') || 'name@example.com'}
              onBlur={(event: FocusEvent) => handleFieldBlur(event, formik, setFormState, 'requestDemo')}
            />

            <TextInput
              backgroundColor={backgroundColor}
              id="demoForm-phone"
              label={t('organisms_forms_fields_phone_label')}
              name="phone"
              type="tel"
              placeholder={t('organisms_forms_fields_phone_placeholder') || 'Enter your phone number'}
              onBlur={(event: FocusEvent) => handleFieldBlur(event, formik, setFormState, 'requestDemo')}
            />

            <TextInput
              backgroundColor={backgroundColor}
              id="demoForm-companyUrl"
              label={t('organisms_forms_fields_company_website_label')}
              name="company_url"
              type="url"
              placeholder={t('organisms_forms_fields_company_website_placeholder') || 'https://example.com'}
              onBlur={(event: FocusEvent) => handleFieldBlur(event, formik, setFormState, 'requestDemo')}
            />
            
            <SelectInput
              backgroundColor={backgroundColor}
              id="demoForm-companySize"
              label={t('organisms_forms_fields_company_size_label') || 'Number of contacts'}
              required name="company_size"
              onBlur={(event: FocusEvent) => handleFieldBlur(event, formik, setFormState, 'requestDemo')}
            >
              <option value="" disabled hidden>{t('organisms_forms_fields_company_size_placeholder')}</option>
              <option value="0 - 1K">{t('organisms_forms_fields_company_size_options_zero')}</option>
              <option value="1K - 3K">{t('organisms_forms_fields_company_size_options_one')}</option>
              <option value="3K - 25K">{t('organisms_forms_fields_company_size_options_three')}</option>
              <option value="25K - 100K">{t('organisms_forms_fields_company_size_options_twentyFive')}</option>
              <option value="100K - 500K">{t('organisms_forms_fields_company_size_options_oneHundred')}</option>
              <option value="500K - 1M">{t('organisms_forms_fields_company_size_options_fiveHundred')}</option>
              <option value="1M+">{t('organisms_forms_fields_company_size_options_oneMillion')}</option>
            </SelectInput>
            <div className={styles.marginBottom25}>
              <CheckboxInput backgroundColor={backgroundColor} id="demoForm-newsletter" label={t('organisms_forms_fields_newsletter_label') || 'Tell me more. Sign me up for marketing communications from Klaviyo.*'} size="small" name="field_1" onChange={(event: FormEvent) => handleFieldBlur(event, formik, setFormState, 'requestDemo')} />
            </div>
            <div className={styles.marginBottom25}>
              <Reaptcha
                hl={t('langCodes_recaptchaLanguage')}
                ref={recaptchaInstance}
                sitekey={process.env.GATSBY_RECAPTCHA_KEY}
                onVerify={(response) => {
                  formik.setFieldValue('g-recaptcha-response', response);
                }}
                theme={backgroundColor}
              />
            </div>
            <HiddenInput id="utm-source-demo" name="utm_source" />
            <HiddenInput id="utm-medium-demo" name="utm_medium" />
            <HiddenInput id="utm-campaign-demo" name="utm_campaign" />
            <HiddenInput id="utm-content-demo" name="utm_content" />
            <HiddenInput id="utm-term-demo" name="utm_term" />
            <HiddenInput id="gclid-demo" name="gclid" />
            <HiddenInput id="detail-demo" name="detail" />
            <HiddenInput id="wbraid-demo" name="wbraid" />
            <HiddenInput id="first-page-demo" name="first_page" />
            <HiddenInput id="lead-source-demo" name="lead_source" />
            <HiddenInput id="lead-source-detail-demo" name="lead_source_detail" />
            <HiddenInput id="num-marketing-fields-demo" name="num_marketing_fields" />
            <HiddenInput id="field-6-demo" name="field_6" />
            <HiddenInput id="field-9-demo" name="field_9" />
            <HiddenInput id="field-14-demo" name="field_14" />
            {formStateAdditionalFields?.length > 0 && formStateAdditionalFields.map((field) => (
              <HiddenInput id={`${field.replace('_', '-')}-demo`} name={field} key={field} />
            ))}

            <Button
              text={formik.isSubmitting ? t('organisms_forms_demo_buttonSubmitting') || 'Submitting...' : t('organisms_forms_demo_button') || 'Get a demo'}
              buttonColor={backgroundColor === 'dark' ? 'light' : 'dark'}
              variant="primary"
              type="submit"
            />
            <CTALegalLanguage colorVariant={backgroundColor === 'dark' ? 'light' : 'dark'} formType="demo" />
            <AutofillSync formik={formik} fields={['email']} />
          </Form>
        )}
      </Formik>
    </div>
  );
};

/* @ts-ignore: optimizely */
export default withOptimizely(DemoForm);

